
import React, { useState, useMemo } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { TimeSlot } from "@/models/Schedule";
import { useClients } from "@/hooks/clients/useClientsList";
import { useFuzzyClientSearch } from "@/hooks/clients/useFuzzyClientSearch";
import { useAddClientToBooking } from "@/hooks/useBookings";
import { useAddRecurringBooking } from "@/hooks/useRecurringBookings";
import { useTrialClients } from "@/hooks/useTrialClients";
import { useBookingValidation } from "@/hooks/useBookingValidation";
import { useIsMobile, useIsTablet } from "@/hooks/use-mobile";
import { useTranslation } from "react-i18next";
import { Search, User, Repeat, UserPlus, AlertTriangle } from "lucide-react";
import { WorkoutType } from "@/models/WorkoutType";

interface AddClientDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  timeSlot: TimeSlot | null;
  date: Date;
  onClientAdded: () => void;
  workoutType?: WorkoutType;
}

const AddClientDialog = ({ open, onOpenChange, timeSlot, date, onClientAdded, workoutType }: AddClientDialogProps) => {
  const isMobile = useIsMobile();
  const isTablet = useIsTablet();
  const isFullScreen = isMobile || isTablet;
  const { t } = useTranslation();
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedClientId, setSelectedClientId] = useState<string>("");
  const [isRecurring, setIsRecurring] = useState(false);
  const [trialClientName, setTrialClientName] = useState("");
  const [activeTab, setActiveTab] = useState("regular");
  const [validationError, setValidationError] = useState<string | null>(null);

  const { data: clients = [], isLoading: isLoadingClients } = useClients();
  const filteredClients = useFuzzyClientSearch(clients, searchTerm);
  const addClientToBookingMutation = useAddClientToBooking();
  const addRecurringBookingMutation = useAddRecurringBooking();
  const { addTrialClient, isAddingTrialClient } = useTrialClients();
  const { validateClientBooking, hasBookingConflict } = useBookingValidation(date);

  const selectedClient = useMemo(() => {
    return clients.find(client => client.id === selectedClientId);
  }, [clients, selectedClientId]);

  // Filter clients to show only those with active subscriptions for this workout type
  const eligibleClients = useMemo(() => {
    if (!workoutType) return filteredClients;
    
    return filteredClients.filter(client => 
      client.subscriptions?.some(sub => 
        sub.workoutTypeId === workoutType.id && 
        sub.isActive &&
        (!sub.endDate || new Date(sub.endDate) >= date)
      )
    );
  }, [filteredClients, workoutType, date]);

  // Get day of week for recurring bookings
  const getDayOfWeek = (date: Date): string => {
    const days = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
    return days[date.getDay()];
  };

  const handleAddClient = async () => {
    if (activeTab === "trial") {
      await handleAddTrialClient();
      return;
    }

    if (!selectedClientId || !timeSlot || !workoutType) {
      return;
    }

    // Frontend validation: Check for booking conflicts
    const conflict = validateClientBooking(selectedClientId, timeSlot.startTime, workoutType.id);
    if (conflict) {
      let errorMessage = '';
      if (conflict.type === 'regular') {
        errorMessage = t('calendar.clientAlreadyBookedRegular');
      } else if (conflict.type === 'recurring') {
        errorMessage = t('calendar.clientAlreadyBookedRecurring');
      }
      setValidationError(errorMessage);
      return;
    }

    setValidationError(null);

    try {
      if (isRecurring) {
        await addRecurringBookingMutation.mutateAsync({
          clientId: selectedClientId,
          workoutTypeId: workoutType.id,
          dayOfWeek: getDayOfWeek(date),
          timeSlot: timeSlot.startTime,
          startDate: date,
        });
      } else {
        await addClientToBookingMutation.mutateAsync({
          clientId: selectedClientId,
          timeSlotId: timeSlot.id,
          bookingDate: date,
          workoutTypeId: workoutType.id,
        });
      }

      resetForm();
      onClientAdded();
      onOpenChange(false);
    } catch (error) {
      // Error handling is done in the mutation
    }
  };

  const handleClientSelect = (clientId: string) => {
    setSelectedClientId(clientId);
    
    // Clear validation error when client changes
    setValidationError(null);
    
    // Check for conflicts immediately when client is selected
    if (!timeSlot || !workoutType) return;
    
    const conflict = validateClientBooking(clientId, timeSlot.startTime, workoutType.id);
    if (conflict) {
      let errorMessage = '';
      if (conflict.type === 'regular') {
        errorMessage = t('calendar.clientAlreadyBookedRegular');
      } else if (conflict.type === 'recurring') {
        errorMessage = t('calendar.clientAlreadyBookedRecurring');
      }
      setValidationError(errorMessage);
    }
  };

  const handleRecurringChange = (checked: boolean | "indeterminate") => {
    setIsRecurring(checked === true);
  };

  const handleAddTrialClient = async () => {
    if (!trialClientName.trim() || !timeSlot || !workoutType) return;

    try {
      await addTrialClient({
        trialClientName: trialClientName.trim(),
        timeSlotId: timeSlot.id,
        bookingDate: date,
        workoutTypeId: workoutType.id,
      });

      resetForm();
      onClientAdded();
      onOpenChange(false);
    } catch (error) {
      console.error("Error adding trial client:", error);
    }
  };

  const resetForm = () => {
    setSearchTerm("");
    setSelectedClientId("");
    setIsRecurring(false);
    setTrialClientName("");
    setActiveTab("regular");
    setValidationError(null);
  };

  const content = (
    <>
      {timeSlot && (
        <div className="space-y-4 py-2">
          <div className="text-sm space-y-1">
            <div><strong>{t('common.date')}:</strong> {date.toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' })}</div>
            <div><strong>{t('common.time')}:</strong> {timeSlot.startTime} - {timeSlot.endTime}</div>
            {workoutType && (
              <div className="flex items-center gap-2">
                <strong>{t('calendar.workoutType')}:</strong>
                <Badge 
                  variant="secondary"
                  style={{ 
                    backgroundColor: workoutType.color,
                    color: 'white'
                  }}
                >
                  {workoutType.name}
                </Badge>
              </div>
            )}
          </div>
          
          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="regular" className="flex items-center gap-2">
                <User className="h-4 w-4" />
                {t('calendar.regularClient')}
              </TabsTrigger>
              <TabsTrigger value="trial" className="flex items-center gap-2">
                <UserPlus className="h-4 w-4" />
                {t('calendar.trialClient')}
              </TabsTrigger>
            </TabsList>
            
            <TabsContent value="regular" className="space-y-4">
              {/* Search Input */}
              <div className="space-y-2">
                <Label htmlFor="client-search">{t('calendar.searchClients')}</Label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="client-search"
                    placeholder={t('calendar.searchPlaceholder')}
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10"
                  />
                </div>
              </div>

              {/* Client List */}
              <div className="space-y-2">
                <Label>{t('calendar.selectClient')}</Label>
                <ScrollArea className="h-48 border rounded-md">
                  {isLoadingClients ? (
                    <div className="p-4 text-center text-sm text-muted-foreground">
                      {t('common.loading')}
                    </div>
                  ) : eligibleClients.length === 0 ? (
                    <div className="p-4 text-center text-sm text-muted-foreground">
                      {searchTerm ? t('calendar.noClientsFound') : t('calendar.noEligibleClients')}
                    </div>
                   ) : (
                     <div className="p-2 space-y-1">
                       {validationError && (
                         <div className="text-sm text-destructive bg-destructive/10 p-2 rounded mb-2">
                           {validationError}
                         </div>
                       )}
                       {eligibleClients.map((client) => {
                         const hasConflict = timeSlot && workoutType 
                           ? hasBookingConflict(client.id, timeSlot.startTime, workoutType.id)
                           : false;
                         return (
                           <div
                             key={client.id}
                             className={`p-3 rounded-lg cursor-pointer transition-colors ${
                               selectedClientId === client.id
                                 ? 'bg-primary text-primary-foreground'
                                 : hasConflict 
                                   ? 'bg-destructive/10 border border-destructive/20 opacity-60'
                                   : 'hover:bg-muted'
                             }`}
                             onClick={() => !hasConflict && handleClientSelect(client.id)}
                           >
             <div className="flex items-center gap-3">
                               <User className="h-4 w-4 flex-shrink-0" />
                               <div className="flex-1 min-w-0">
                                 <div className="font-medium truncate">
                                   {client.firstName} {client.lastName}
                                 </div>
                                 <div className="text-sm opacity-70 truncate">
                                   {client.phone}
                                   {client.email && ` • ${client.email}`}
                                 </div>
                                 {hasConflict && (
                                   <div className="text-xs text-destructive mt-1">
                                     {t('calendar.clientConflictWarning')}
                                   </div>
                                 )}
                               </div>
                               {hasConflict && (
                                 <AlertTriangle className="h-4 w-4 text-destructive" />
                               )}
                             </div>
                           </div>
                          );
                        })}
                      </div>
                  )}
                </ScrollArea>
              </div>

              {/* Recurring booking checkbox */}
              {selectedClient && (
                <div className="flex items-center space-x-2 p-3 bg-muted/50 rounded-lg">
                  <Checkbox
                    id="recurring-booking"
                    checked={isRecurring}
                    onCheckedChange={handleRecurringChange}
                  />
                  <Label htmlFor="recurring-booking" className="text-sm flex items-center gap-2">
                    <Repeat className="h-4 w-4" />
                    {t('calendar.makeRecurring')}
                  </Label>
                </div>
              )}
            </TabsContent>
            
            <TabsContent value="trial" className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="trial-client-name">{t('calendar.trialClientName')}</Label>
                <Input
                  id="trial-client-name"
                  placeholder={t('calendar.enterFullName')}
                  value={trialClientName}
                  onChange={(e) => setTrialClientName(e.target.value)}
                />
              </div>
              <div className="p-3 bg-muted/50 rounded-lg">
                <p className="text-sm text-muted-foreground">
                  {t('calendar.trialClientNote')}
                </p>
              </div>
            </TabsContent>
          </Tabs>
        </div>
      )}
    </>
  );

  const isRegularClientValid = activeTab === "regular" && selectedClientId && !validationError;
  const isTrialClientValid = activeTab === "trial" && trialClientName.trim();
  const isFormValid = isRegularClientValid || isTrialClientValid;
  const isLoading = addClientToBookingMutation.isPending || addRecurringBookingMutation.isPending || isAddingTrialClient;

  const actions = (
    <Button 
      onClick={handleAddClient} 
      className="w-full"
      disabled={!isFormValid || isLoading}
    >
      {isLoading 
        ? (activeTab === "trial" ? t('calendar.addingTrialClient') : t('calendar.booking')) 
        : (activeTab === "trial" ? t('calendar.addTrialClient') : t('calendar.addClient'))
      }
    </Button>
  );

  if (isFullScreen) {
    return (
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent side="bottom" className="h-[100dvh] flex flex-col p-0">
          <SheetHeader className="shrink-0 text-left px-4 pt-4">
            <SheetTitle>{t('calendar.addClientToSession')}</SheetTitle>
          </SheetHeader>
          
          <div className="flex-1 overflow-y-auto px-4">
            {content}
            
            {/* Footer buttons inside scrollable area */}
            <div className="sticky bottom-0 bg-background pt-4 pb-4 safe-bottom border-t mt-4">
              {actions}
            </div>
          </div>
        </SheetContent>
      </Sheet>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{t('calendar.addClientToSession')}</DialogTitle>
        </DialogHeader>
        
        {content}
        
        <DialogFooter>
          <Button 
            onClick={handleAddClient}
            disabled={!isFormValid || isLoading}
          >
            {isLoading 
              ? (activeTab === "trial" ? t('calendar.addingTrialClient') : t('calendar.booking')) 
              : (activeTab === "trial" ? t('calendar.addTrialClient') : t('calendar.addClient'))
            }
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default AddClientDialog;
